import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import {
  AppState,
  UserProfile,
  Application,
  AppSettings,
  SavedQuestion,
  saveState,
} from "@/lib/storage";

const DEFAULT_PROFILE: UserProfile = {
  personal: {
    fullName: "",
    email: "",
    phone: "",
    location: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    postcode: "",
    nationality: "",
    dateOfBirth: "",
    gender: "",
    workAuthorization: "",
    yearsOfExperience: "",
    seniorityLevel: "",
    timeZone: "",
    linkedIn: "",
    portfolio: "",
    github: "",
  },
  summary: "",
  experience: [],
  education: [],
  skills: [],
  commonAnswers: {},
};

const DEFAULT_STATE: AppState = {
  profile: DEFAULT_PROFILE,
  applications: [],
  savedQuestions: [],
  settings: {
    theme: "system",
    currency: "USD" as const,
    subscription: {
      status: "trial",
      trialStart: new Date().toISOString(),
      expiresAt: new Date(
        Date.now() + 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
    },
  },
  onboarded: false,
};

export function useProfileSync() {
  const { user } = useAuth();
  const [state, setState] = useState<AppState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Load from DB
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (data) {
        const rawProfile =
          (data.profile_data as unknown as Partial<UserProfile>) || {};
        // Deep merge with defaults to handle new fields from updates
        const mergedProfile: UserProfile = {
          personal: {
            ...DEFAULT_PROFILE.personal,
            ...rawProfile.personal,
          },
          summary: rawProfile.summary ?? DEFAULT_PROFILE.summary,
          experience: rawProfile.experience ?? DEFAULT_PROFILE.experience,
          education: rawProfile.education ?? DEFAULT_PROFILE.education,
          skills: rawProfile.skills ?? DEFAULT_PROFILE.skills,
          commonAnswers:
            rawProfile.commonAnswers ?? DEFAULT_PROFILE.commonAnswers,
        };
        const loaded: AppState = {
          profile: mergedProfile,
          applications:
            (data.applications as unknown as Application[]) || [],
          savedQuestions:
            ((data.settings as any)?.savedQuestions as SavedQuestion[]) ||
            [],
          settings: {
            ...DEFAULT_STATE.settings,
            ...(data.settings as unknown as Partial<AppSettings>),
          },
          onboarded: data.onboarded,
        };
        setState(loaded);
        saveState(loaded); // Seed localStorage so extension auto-fill has fresh data
      }
      setLoading(false);
    };
    load();
  }, [user]);

  // Debounced save
  const saveToDB = useCallback(
    (stateToSave: AppState) => {
      if (!user) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(() => {
        supabase
          .from("profiles")
          .update({
            profile_data: JSON.parse(JSON.stringify(stateToSave.profile)),
            applications: JSON.parse(
              JSON.stringify(stateToSave.applications)
            ),
            settings: JSON.parse(JSON.stringify(stateToSave.settings)),
            onboarded: stateToSave.onboarded,
          })
          .eq("user_id", user.id)
          .then();
      }, 1000);
    },
    [user]
  );

  const updateState = useCallback(
    (newState: AppState | ((prev: AppState) => AppState)) => {
      setState((prev) => {
        const next =
          typeof newState === "function" ? newState(prev) : newState;
        saveToDB(next);
        saveState(next); // Keep localStorage in sync for extension auto-fill
        return next;
      });
    },
    [saveToDB]
  );

  return { state, updateState, loading };
}
